import { DirectoryEntry } from '@ionic-native/file';
import { hammerReducer } from './hammer.reducer';
import { fileNameFilter, getVisibleItemIds } from './helper';
import { MediaPickerFiltersModel } from './media-picker-filters.model';
import { MediaPickerItemModel } from './media-picker-item.model';
import * as actions from './media-picker-modal.actions';
import { ImageGridMode, TouchState } from './media-picker-modal.types';
import { MediaPickerPageInfoModel } from './media-picker-page-info.model';

export interface State extends MediaPickerPageInfoModel {
    ids: Array<string>;
    entities: {[id: string]: MediaPickerItemModel};
    root: string;
    dirEntries: Array<DirectoryEntry>;
    currentDir: string;
    filters: MediaPickerFiltersModel;
    selectMode: boolean;
    imageGridMode: ImageGridMode;
    touchState: TouchState;
    selectFromId: string;
    visibleIds: Array<string>;
}

export const initialState: State = {
    ids: [],
    entities: {},
    root: '',
    dirEntries: [],
    currentDir: '/DCIM/',
    filters: {
        lastModifiedDate: 0,
        image: true,
        audio: false,
        video: false,
        pdf: false,
        other: false
    },
    selectMode: undefined,
    imageGridMode: 'scrolling',
    touchState: 'up',
    selectFromId: undefined,
    page: 0,
    pageSize: 0,
    visibleIds: []
};

// tslint:disable-next-line:only-arrow-functions
export function reducer(
    state = initialState,
    action: actions.Actions
): State {
    switch (action.type) {
        case actions.CANCEL:
        case actions.SUBMIT: {
            return {
                ...initialState
            };
        }
        case actions.ADD_FILE_MODELS: {
            const ids = [];
            const timestamp = Date.now();
            const models = action.payload.models
                // exclude invalid filenames from new models
                .filter(model => fileNameFilter(model.name))
                // set the timestamp for new models
                .map(model => ({...model, timestamp}))
                // concatenate with the existing models
                .concat(state.ids.map(id => state.entities[id]))
                // sort the whole list from newest to oldest
                .sort((a, b) => b.lastModifiedDate - a.lastModifiedDate);

            // reduce to a dictionary of entities
            const entities = models
                .reduce((p, c) => {
                    p[c.id] = c;
                    ids.push(c.id);
                    return p;
                }, {} as {[id: string]: MediaPickerItemModel});

            // calculate the list of visible items
            const visibleIds = getVisibleItemIds(
                models,
                state.filters,
                {page: state.page, pageSize: state.pageSize}
            );

            return {
                ...state,
                ids,
                entities,
                visibleIds
            };
        }
        case actions.CANCEL_SELECTION: {
            return {
                ...state,
                imageGridMode: 'scrolling',
                selectFromId: undefined,
                selectMode: undefined,
                touchState: 'up',
                entities: state.ids
                    .reduce((p, c) => {
                        p[c] = {...state.entities[c], selected: false};
                        return p;
                    }, {})
            };
        }
        case actions.INITIALISE: {
            return {
                ...initialState,
                pageSize: action.payload.gridInfo.pageSize,
                page: 1
            };
        }
        case actions.INITIALISE_SUCCESS: {
            return {
                ...state,
                root: action.payload.root,
                dirEntries: action.payload.dirEntries
                    .filter(entry => !entry.name.startsWith('.'))
            };
        }
        case actions.HAMMER: {
            return hammerReducer(state, action);
        }
        case actions.LOAD_MORE: {

            const models = state.ids.map(id => state.entities[id]);

            // calculate the list of visible items
            const visibleIds = getVisibleItemIds(
                models,
                state.filters,
                {page: state.page + 1, pageSize: state.pageSize}
            );

            return {
                ...state,
                page: state.page + 1,
                visibleIds
            };
        }
        case actions.MAKE_THUMBNAIL_SUCCESS: {
            return {
                ...state,
                entities: {
                    ...state.entities,
                    [action.payload.model.id]: {
                        ...action.payload.model,
                        timestamp: Date.now()
                    }
                }
            };
        }
        case actions.SET_CURRENT_DIR: {
            return {
                ...state,
                currentDir: action.payload.dirName
            };
        }
        case actions.SET_FILTERS: {

            const models = state.ids.map(id => state.entities[id]);

            // calculate the list of visible items
            const visibleIds = getVisibleItemIds(
                models,
                action.payload.filters,
                {page: state.page + 1, pageSize: state.pageSize}
            );

            return {
                ...state,
                filters: action.payload.filters,
                visibleIds
            };
        }
        case actions.TAP: {
            return {
                ...state,
                entities: {
                    ...state.entities,
                    [action.payload.id]: {
                        ...state.entities[action.payload.id],
                        selected: !state.entities[action.payload.id],
                        timestamp: Date.now()
                    }
                }
            };
        }
        default: {
            return state;
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/modals-popovers/media-picker-modal/media-picker-modal.reducer.ts